import { Schema } from "effect";
import { NewUiComponent } from "./conversation/message/ui-component";

/**
 * Stream events emitted by `ReplyAgentExecutionService.generateDraft` as it
 * drives an agent on a channel thread. Thread-shaped — events do not depend
 * on a specific topic kind.
 *
 * IDs are encoded as `Schema.String` here rather than the branded
 * `ChannelThreadId` / `MessageId` schemas because these events are
 * wire-format DTOs; consumers that need branded IDs can re-decode with the
 * branded schemas at the consumption site.
 */

export const AiReasoningNotification = Schema.Struct({
  _tag: Schema.Literal("aiReasoning"),
  threadId: Schema.String,
  text: Schema.String,
});
export type AiReasoningNotification = typeof AiReasoningNotification.Type;

export const AiToolCallNotification = Schema.Struct({
  _tag: Schema.Literal("aiToolCall"),
  threadId: Schema.String,
  toolName: Schema.String,
  toolCallId: Schema.String,
});
export type AiToolCallNotification = typeof AiToolCallNotification.Type;

export const AiTextDeltaNotification = Schema.Struct({
  _tag: Schema.Literal("aiTextDelta"),
  threadId: Schema.String,
  textDelta: Schema.String,
});
export type AiTextDeltaNotification = typeof AiTextDeltaNotification.Type;

export const AiStatusNotification = Schema.Struct({
  _tag: Schema.Literal("aiStatus"),
  threadId: Schema.String,
  status: Schema.String,
});
export type AiStatusNotification = typeof AiStatusNotification.Type;

export const AiMessageAddedNotification = Schema.Struct({
  _tag: Schema.Literal("aiMessageAdded"),
  threadId: Schema.String,
  messageId: Schema.String,
  contentType: Schema.String,
});
export type AiMessageAddedNotification = typeof AiMessageAddedNotification.Type;

const AiMessagePreviewFile = Schema.Struct({
  url: Schema.String,
  description: Schema.String,
  mimeType: Schema.NullOr(Schema.String),
});

export const AiMessagePreviewPart = Schema.Union(
  Schema.TaggedStruct("text", {
    content: Schema.String,
    files: Schema.Array(AiMessagePreviewFile),
  }),
  Schema.TaggedStruct("uiComponent", {
    content: Schema.String,
    component: NewUiComponent,
  }),
);
export type AiMessagePreviewPart = typeof AiMessagePreviewPart.Type;

/** The messages an agent saved to send, in order, so the widget shows them before they arrive. */
const AiMessagePreviewNotification = Schema.Struct({
  _tag: Schema.Literal("aiMessagePreview"),
  threadId: Schema.String,
  parts: Schema.Array(AiMessagePreviewPart),
});

export const AiActivityNotification = Schema.Union(
  AiReasoningNotification,
  AiToolCallNotification,
  AiTextDeltaNotification,
  AiStatusNotification,
  AiMessageAddedNotification,
  AiMessagePreviewNotification,
);
export type AiActivityNotification = typeof AiActivityNotification.Type;

export type AiActivityNotificationTag = AiActivityNotification["_tag"];
